import { Capacitor } from '@capacitor/core';
import { motion } from 'framer-motion';
import {
  History,
  Search,
  Eye,
  Download,
  Calendar,
  Building,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronUp,
  Send,
  Mail,
  Loader2,
  Copy,
  Check,
  Zap,
  X,
  AlertTriangle,
  Hourglass,
  CheckCircle2,
  ExternalLink,
  MoreVertical,
  Clock,
} from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { MobileHistoryCard } from '../../../components/mobile';
import { usePlanStatus } from '../../../hooks/usePlanStatus';
import { supabase } from '../../../lib/supabase';
import {
  getJobApplications,
  saveAgentArtifacts,
  updateApplicationStatus,
} from '../../../services/applicationTrackingService';
import type { JobApplication } from '../../../services/applicationTrackingService';
import { logEvent } from '../../../services/loggingService';
import { pushUserNotification } from '../../../services/objectivesTasksService';
import { getOptimizationHistory } from '../../../services/optimizationService';
import type { OptimizationResult } from '../../../types/optimization';
import { getCurrentUser } from '../../../utils/authUtils';
import { normalizeResumeText } from '../../../utils/pdfTextSanitizer';
import { generateResumePDF, generateResumeDOCX } from '../../../utils/resumeDocumentGenerator';
import type { TemplateId } from '../../../utils/resumeDocumentGenerator';
import { useApplyReturnNudge } from '../../ai-agent/hooks/useApplyReturnNudge';

import { getTemplateSkinClass } from './AIResumeOptimizer/ResumePreviewForPDF';
import ResumePreviewForPDF from './AIResumeOptimizer/ResumePreviewForPDF';
import {
  parseResumeSections,
  sortSectionsByCanonicalOrder,
  normalizeSectionTitles,
} from './AIResumeOptimizer/utils/parseResumeSections';
import { SectionContentWithFormatting } from './AIResumeOptimizer/utils/sectionContentRenderer';


interface OptimizationHistoryProps {
  onNavigate?: (page: string) => void;
}

/** Format cover letter or application letter for display: add header, salutation, and signature when missing */
/**
 * Empty-state placeholder for an artifact drawer tab when no content exists
 * (e.g. a job that has a saved resume but no cover letter yet).
 */
const EmptyTabState: React.FC<{ label: string }> = ({ label }) => (
  <div className="ds-artifact-drawer__empty flex flex-col items-center justify-center text-center px-8 py-16">
    <div className="ds-artifact-drawer__empty-icon" aria-hidden="true">—</div>
    <p className="ds-artifact-drawer__empty-label">{label}</p>
  </div>
);

function formatLetterForDisplay(
  title: string,
  body: string,
  opts: { letterLabel: string; salutation: string; closing: string; dateStr: string }
): string {
  const trimmed = (body || '').trim();
  if (!trimmed) return body;
  const hasSalutation = /^\s*Dear\s+/i.test(trimmed);
  const hasClosing = /\n\s*(Sincerely|Best regards|Regards|Yours sincerely|Respectfully),?\s*$/im.test(trimmed);

  let out = `${opts.letterLabel}\n${'—'.repeat(32)}\n\n${opts.dateStr}\n\n`;
  if (!hasSalutation) out += opts.salutation + '\n\n';
  out += trimmed;
  if (!hasClosing) out += '\n\n' + opts.closing;
  return out;
}

// ─────────────────────────────────────────────────────────────────────
// Next-Step resolver — derives the action a user needs to take for a row.
//
// Drives the STATUS column rendering: every row maps to one of a small
// set of states so a user with 77 rows can scan the column and instantly
// see which jobs need them and which don't. The `needsAction` flag is
// also used by the "Needs Action" filter chip in the toolbar.
// ─────────────────────────────────────────────────────────────────────

type NextStepKey =
  | 'needs_apply'      // pending / saved_for_later / no row yet
  | 'confirm_apply'    // in_progress — portal opened, awaiting confirmation
  | 'awaiting_reply'   // applied recently (< FOLLOWUP_DAYS)
  | 'followup_due'     // applied >= FOLLOWUP_DAYS ago, no response
  | 'interview'        // interviewing
  | 'offer'            // offered / accepted
  | 'closed';          // rejected / declined / withdrawn / not_applied

/** Glance-test palette — warm = act now, cool = waiting, green = won/closed loop */
type StepTone = 'action' | 'pending' | 'success' | 'neutral';

interface NextStepInfo {
  key:           NextStepKey;
  label:         string;
  tone:          StepTone;
  needsAction:   boolean;
  /** Subtle "agent monitoring" pulse on the status pill */
  pulse:         boolean;
}

/** Activity status pill — theme-synced typography + colors (matches Job Details) */
function stepStatusClass(tone: StepTone): string {
  switch (tone) {
    case 'action':  return 'ds-activity-status ds-activity-status--action';
    case 'success': return 'ds-activity-status ds-activity-status--success';
    case 'pending': return 'ds-activity-status ds-activity-status--pending';
    default:        return 'ds-activity-status ds-activity-status--neutral';
  }
}

const FOLLOWUP_DAYS = 7;

function nextStepFor(app: JobApplication | undefined): NextStepInfo {
  const status = app?.application_status;
  if (!app || !status || status === 'saved_for_later' || status === 'not_applied') {
    if (status === 'not_applied') {
      return { key: 'closed', label: 'Closed', tone: 'neutral', needsAction: false, pulse: false };
    }
    return { key: 'needs_apply', label: 'Needs Apply', tone: 'action', needsAction: true, pulse: false };
  }
  if (status === 'in_progress') {
    return { key: 'confirm_apply', label: 'Confirm Apply', tone: 'action', needsAction: true, pulse: true };
  }
  if (status === 'applied') {
    const appliedAt = app.applied_date ? new Date(app.applied_date).getTime() : Date.now();
    const days = (Date.now() - appliedAt) / 86_400_000;
    if (days >= FOLLOWUP_DAYS) {
      return { key: 'followup_due', label: 'Follow-up Due', tone: 'action', needsAction: true, pulse: false };
    }
    return { key: 'awaiting_reply', label: 'Awaiting Reply', tone: 'pending', needsAction: false, pulse: true };
  }
  if (status === 'interviewing') {
    return { key: 'interview', label: 'Interview Prep', tone: 'action', needsAction: true, pulse: true };
  }
  if (status === 'offered' || status === 'accepted') {
    return { key: 'offer', label: 'Offer', tone: 'success', needsAction: false, pulse: false };
  }
  return { key: 'closed', label: 'Closed', tone: 'neutral', needsAction: false, pulse: false };
}

/** Short display title + optional long tail (parentheses, scraped fluff). */
function splitJobDisplay(rawTitle: string): { short: string; extra: string | null; full: string } {
  const full = rawTitle.trim() || '—';
  const paren = full.match(/^(.+?)\s*(\(.+\))\s*$/);
  if (paren && paren[1].length >= 4 && paren[2].length > 12) {
    return { short: paren[1].trim(), extra: paren[2].trim(), full };
  }
  if (full.length > 52) {
    return { short: `${full.slice(0, 49).trim()}…`, extra: full, full };
  }
  return { short: full, extra: null, full };
}

function resolveJobUrl(opt: { jobUrl?: string; job_url?: string }, app?: JobApplication): string | undefined {
  return opt.jobUrl || opt.job_url || app?.job_url || undefined;
}

type HoverMenuItem = { id: string; label: string; onClick: () => void; danger?: boolean };

function buildStepHoverItems(
  step: NextStepInfo,
  jobUrl: string | undefined,
  handlers: { onOpenPortal: () => void; onConfirmNotApplied: () => void; onReopenBundle: () => void },
): HoverMenuItem[] {
  const items: HoverMenuItem[] = [];
  if (step.key === 'confirm_apply') {
    items.push({ id: 'not_applied', label: "Didn't apply", onClick: handlers.onConfirmNotApplied, danger: true });
    items.push({ id: 'reopen', label: 'Re-run apply bundle', onClick: handlers.onReopenBundle });
  }
  if (!jobUrl && (step.key === 'needs_apply' || step.key === 'confirm_apply')) {
    items.push({ id: 'portal', label: 'Open apply bundle', onClick: handlers.onOpenPortal });
  }
  return items;
}

function buildDocumentMenuItems(
  coverText: string,
  appLetterText: string,
  onNavigate: OptimizationHistoryProps['onNavigate'],
  handlers: {
    onPreviewCover: () => void;
    onPreviewApplication: () => void;
    onGenerateCover: () => void;
    onGenerateApplication: () => void;
  },
): HoverMenuItem[] {
  const items: HoverMenuItem[] = [];
  if (coverText) {
    items.push({ id: 'cover', label: 'Cover letter', onClick: handlers.onPreviewCover });
  } else if (onNavigate) {
    items.push({ id: 'gen_cover', label: '+ Cover letter', onClick: handlers.onGenerateCover });
  }
  if (appLetterText) {
    items.push({ id: 'app_letter', label: 'Application letter', onClick: handlers.onPreviewApplication });
  } else if (onNavigate) {
    items.push({ id: 'gen_app', label: '+ Application letter', onClick: handlers.onGenerateApplication });
  }
  return items;
}

function stepStatusIcon(step: NextStepInfo): React.ReactNode {
  switch (step.key) {
    case 'confirm_apply': return <Hourglass className="w-3 h-3 shrink-0" />;
    case 'awaiting_reply': return <Clock className="w-3 h-3 shrink-0" />;
    case 'followup_due': return <AlertTriangle className="w-3 h-3 shrink-0" />;
    case 'offer': return <CheckCircle2 className="w-3 h-3 shrink-0" />;
    default: return null;
  }
}

const OptimizationHistory: React.FC<OptimizationHistoryProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const planStatus = usePlanStatus();
  // Limits: free = 3, paid plans = unlimited
  const historyLimit = planStatus.planKey === 'free' ? 3
    : null; // null = no limit

  const [optimizations, setOptimizations] = useState<any[]>([]);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOptimization, setSelectedOptimization] = useState<any | null>(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  /** Which tab the drawer opens to — clicking the cover / app-letter buttons
   *  in a row should land the user directly on the matching tab instead of
   *  always defaulting to Resume. */
  const [drawerTab, setDrawerTab] = useState<'resume' | 'cover' | 'application'>('resume');
  const [resumeCopied, setResumeCopied] = useState(false);
  /** Brief flash when drawer copy-to-clipboard succeeds (any tab). */
  const [drawerCopied, setDrawerCopied] = useState(false);

  // Upgrade prompt modal state
  const [upgradePrompt, setUpgradePrompt] = useState<{ title: string; message: string } | null>(null);

  const openDocxUpgradePrompt = () => setUpgradePrompt({
    title: 'DOCX Download — Upgrade Required',
    message: 'Exporting your resume as an editable Word (.docx) document is available on the Weekly and Monthly plans. Upgrade now to unlock this feature.',
  });
  const openPdfUpgradePrompt = () => setUpgradePrompt({
    title: 'PDF Download — Credits Required',
    message: 'You have no credits remaining to download a PDF. Purchase a plan to get more credits and download your optimized resumes.',
  });

  // Filtering and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'position' | 'company' | 'date' | 'category'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch optimization history and job applications (to show cover letter, application letter, apply status)
  const fetchOptimizations = async () => {
    try {
      setLoading(true);
      setError(null);

      const [data, appList] = await Promise.all([
        getOptimizationHistory(100, 0),
        getJobApplications({}).catch(() => []),
      ]);

      // ── Merge in "agent-only" job_applications rows ────────────────────────
      // The agent flow writes artifacts directly to `job_applications` without
      // creating an llm_optimization_levelX row. Without this merge, anything
      // optimized through the agent (resume / cover letter / app note) would
      // never appear in the Activity page even though My Activities should
      // surface every preparation the user did. We synthesize lightweight
      // optimization-history-shaped rows so the existing renderer just works.
      const apps = Array.isArray(appList) ? appList : [];
      const knownOptIds = new Set<string>();
      const knownJobIds = new Set<string>();
      for (const opt of data ?? []) {
        if (opt.optimization_id) knownOptIds.add(String(opt.optimization_id));
        if (opt.job_id)          knownJobIds.add(String(opt.job_id));
      }

      const seenSynthetic = new Set<string>();
      const syntheticRows = apps
        .filter((app) => {
          // Skip applications already represented by an optimization_history row
          const optId = (app as { optimization_id?: string }).optimization_id;
          if (optId && knownOptIds.has(String(optId))) return false;
          const jid = (app as { job_id?: string }).job_id;
          if (jid && knownJobIds.has(String(jid))) return false;
          // Only surface rows where the agent actually produced something
          const hasArtifact =
            Boolean((app as { resume_version?: string }).resume_version) ||
            Boolean((app as { cover_letter_snippet?: string }).cover_letter_snippet) ||
            Boolean((app as { application_snippet?: string }).application_snippet);
          if (!hasArtifact) return false;
          // De-dupe by (title|company) to avoid duplicating refines of the same job
          const key = `${(app.job_title || '').toLowerCase().trim()}|${(app.company_name || '').toLowerCase().trim()}`;
          if (seenSynthetic.has(key)) return false;
          seenSynthetic.add(key);
          return true;
        })
        .map((app) => {
          const matchScore = typeof (app as { confidence_score?: number }).confidence_score === 'number'
            ? (app as { confidence_score: number }).confidence_score
            : 0;

          // The agent flow stores the resume as markdown with `## Section` headings.
          // Pre-split it into structured sections so the preview renderer takes
          // the proper sectioned path instead of dumping raw markdown (which
          // leaves visible `##` artifacts at the top of the modal).
          const resumeMd: string = (app as { resume_version?: string }).resume_version ?? '';
          const parsedSections: Array<{ title: string; content: string; preview: string }> = [];
          if (resumeMd) {
            // Split on H2 markers; keep empty intro out.
            const parts = resumeMd.split(/(?:^|\n)##\s+/);
            for (const part of parts) {
              const trimmed = part.trim();
              if (!trimmed) continue;
              const newlineIdx = trimmed.indexOf('\n');
              const title = (newlineIdx === -1 ? trimmed : trimmed.slice(0, newlineIdx)).trim();
              let body  = (newlineIdx === -1 ? ''      : trimmed.slice(newlineIdx + 1)).trim();
              // Strip stray heading-only markers ("##", "###") that the LLM
              // occasionally emits on their own line — they leak through as
              // visible "##" artifacts in the preview.
              body = body
                .split('\n')
                .filter((line) => !/^\s*#{1,6}\s*$/.test(line))
                .join('\n')
                .trim();
              if (!title || !body) continue;
              parsedSections.push({ title, content: body, preview: body.slice(0, 120) });
            }
          }
          // Hand the preview clean text (no `## ` prefixes) so the fallback
          // parser path is also safe if `sections` is somehow ignored.
          const cleanResume = parsedSections.length > 0
            ? parsedSections.map((s) => `${s.title.toUpperCase()}\n${s.content}`).join('\n\n')
            : resumeMd;

          // We don't store an explicit initial score for agent flow yet — leave
          // null so the renderer shows "—" for improvement instead of fabricating.
          return {
            // Make the synthetic id unique (and not a UUID so client code never
            // confuses it with a real optimization_history row).
            optimization_id: `agent-${app.id}`,
            isAgentSynthetic: true,
            /** Direct back-reference so getApplicationForOpt can resolve without a UUID join. */
            _agentApplication: app,
            created_at: app.applied_date || (app as { created_at?: string }).created_at || app.last_updated,
            jobTitle: app.job_title,
            job_title: app.job_title,
            companyName: app.company_name,
            company_name: app.company_name,
            jobUrl: app.job_url,
            job_url: app.job_url,
            job_id: (app as { job_id?: string }).job_id ?? `synthetic-${app.id}`,
            initial_score: null,
            originalScore: null,
            match_score: matchScore,
            optimizedScore: matchScore,
            optimizedResume: cleanResume,
            optimized_resume: cleanResume,
            final_resume:    cleanResume,
            sections: parsedSections.length > 0 ? parsedSections : undefined,
            improvements: [],
            warnings:     [],
            ai_insights:  null,
            selectedTemplateId: 'ats-optimized',
            resume_version_id: null,
          } as any;
        });

      // Combine and sort by created_at desc so the agent's latest activity
      // appears at the top regardless of which sort the user picks later.
      const combined = [...(data ?? []), ...syntheticRows].sort(
        (a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime(),
      );

      setOptimizations(combined);
      setApplications(apps);

      await logEvent({
        level: 'info',
        action: 'optimization_history_loaded',
        details: {
          count: combined.length,
          agentSynthetic: syntheticRows.length,
        },
      });
    } catch (err) {
      console.error('Error fetching optimization history:', err);
      setError(t('dashboard.optimizationHistory.failedToLoad'));
      await logEvent({
        level: 'error',
        action: 'optimization_history_load_error',
        details: { error: err instanceof Error ? err.message : String(err) },
        error: err instanceof Error ? err : new Error(String(err)),
      });
    } finally {
      setLoading(false);
    }
  };

  // Map optimization_id -> job application.
  // Primary key: resume_version_id (set when opt id was a real UUID at apply time).
  // Fallback key: job_id (links applications to optimizations that share the same job).
  const applicationByOptimizationId = useMemo(() => {
    const byResumeVersion: Record<string, JobApplication> = {};
    const byJobId: Record<string, JobApplication> = {};
    applications.forEach((app) => {
      const rid = (app as { resume_version_id?: string }).resume_version_id;
      if (rid) byResumeVersion[rid] = app;
      // Also index by job_id so we can fall back when resume_version_id is missing
      const jid = (app as { job_id?: string }).job_id;
      if (jid && !byJobId[jid]) byJobId[jid] = app; // keep the most recently applied one
    });
    return { byResumeVersion, byJobId };
  }, [applications]);

  const getApplicationForOpt = (opt: any): JobApplication | undefined => {
    // Agent-synthesized rows carry the app reference inline (their job_id
    // may not exist in the indexed maps because external IDs aren't UUIDs).
    if (opt?._agentApplication) return opt._agentApplication as JobApplication;
    return (
      applicationByOptimizationId.byResumeVersion[opt.optimization_id] ??
      applicationByOptimizationId.byJobId[opt.job_id]
    );
  };

  // Load data on mount
  useEffect(() => {
    fetchOptimizations();
  }, []);

  // Esc closes the artifact drawer (matches modal-style affordances)
  useEffect(() => {
    if (!showResumeModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowResumeModal(false);
        setResumeCopied(false);
        setDrawerCopied(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showResumeModal]);

  // Handle sort (date defaults to desc)
  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'date' ? 'desc' : 'asc');
    }
  };

  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  /** Row currently running the Assisted-Handoff orchestration — used to disable the button + show a spinner. */
  const [applyingOptId, setApplyingOptId] = useState<string | null>(null);
  /** Row currently being confirmed (Yes-applied / Didn't apply) — disables the button while writing. */
  const [confirmingAppId, setConfirmingAppId] = useState<string | null>(null);
  /** Toolbar filter: when on, only rows whose next-step requires user action are visible. */
  const [needsActionOnly, setNeedsActionOnly] = useState(false);
  /** Expanded job-detail accordion (long titles / descriptions). */
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  /** Assisted-Handoff return-nudge — same hook as the Agent panel. When the user
   *  clicks Apply, we arm it; when they return after the dwell window, a prompt
   *  appears asking whether they actually submitted. */
  const returnNudge = useApplyReturnNudge();

  const sendReminderEmail = async (applicationId: string) => {
    setSendingReminderId(applicationId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-apply-reminder`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ application_id: applicationId }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        await fetchOptimizations();
      }
    } finally {
      setSendingReminderId(null);
    }
  };

  /**
   * Assisted-Handoff Apply (My Activities entry point).
   *
   * Mirrors `handleCanvasApply` in the Agent panel but runs entirely from
   * the My Activities row — no navigation. The user already optimised the
   * resume; this just bundles + opens + tracks.
   *
   *  1. Copy cover letter to clipboard (fire-and-forget)
   *  2. Trigger PDF download
   *  3. Open the job's external URL in a new tab (same user-gesture call —
   *     no popup block)
   *  4. Persist the row as `application_status = 'in_progress'`
   *  5. Arm the return-nudge (≥30s away → "Did you finish applying?")
   *  6. Push a bell notification confirming the bundle is ready
   *
   * Edge cases:
   *  - No `job_url` → prompts inline; user can paste a URL or cancel
   *  - No cover letter snippet → skip the clipboard step silently
   */
  const handleAssistedApply = async (optimization: any) => {
    const existingApp = getApplicationForOpt(optimization);
    const jobTitle: string = optimization?.jobTitle || optimization?.job_title || existingApp?.job_title || 'this role';
    const companyName: string = optimization?.companyName || optimization?.company_name || existingApp?.company_name || 'this company';
    let jobUrl: string | undefined = optimization?.jobUrl || optimization?.job_url || existingApp?.job_url;
    const coverLetter: string | undefined =
      (existingApp as { cover_letter_snippet?: string } | undefined)?.cover_letter_snippet ||
      existingApp?.cover_letter_version;
    const appNote: string | undefined =
      (existingApp as { application_snippet?: string } | undefined)?.application_snippet ||
      existingApp?.application_notes;

    if (!jobUrl) {
      const pasted = window.prompt(
        `We don't have a portal URL saved for "${jobTitle} @ ${companyName}".\n\nPaste the job listing URL to continue, or cancel:`,
        '',
      );
      if (!pasted) return;
      const trimmed = pasted.trim();
      if (!/^https?:\/\//i.test(trimmed)) {
        window.alert('That doesn\'t look like a valid URL. Please try again with a link that starts with http(s)://');
        return;
      }
      jobUrl = trimmed;
    }

    setApplyingOptId(optimization.optimization_id ?? optimization.job_id ?? jobTitle);

    // Fire-and-forget clipboard copy. We don't await before window.open so
    // the user-gesture chain stays intact for the popup.
    const clipboardPromise = coverLetter
      ? navigator.clipboard
          .writeText(coverLetter)
          .then(() => true)
          .catch(() => false)
      : Promise.resolve(false);

    // Trigger the PDF download in the same gesture. This dispatches the
    // download but doesn't await it — file generation runs in parallel.
    void handleDownload('pdf', optimization);

    let portalOpened = false;
    try {
      const win = window.open(jobUrl, '_blank', 'noopener,noreferrer');
      portalOpened = !!win;
    } catch (e) {
      console.warn('[OptimizationHistory] window.open failed:', e);
    }

    try {
      const clipboardCopied = await clipboardPromise;

      const optimizationId: string | undefined =
        typeof optimization?.optimization_id === 'string' && !optimization.optimization_id.startsWith('agent-')
          ? optimization.optimization_id
          : undefined;

      const saved = await saveAgentArtifacts({
        jobId:          String(optimization?.job_id ?? existingApp?.job_id ?? `synthetic-${optimization?.optimization_id ?? Date.now()}`),
        jobTitle,
        companyName,
        jobUrl,
        sourceChannel:  existingApp?.source_channel ?? 'other',
        coverLetter,
        applicationNote: appNote,
        matchScore:     typeof optimization?.optimizedScore === 'number' ? optimization.optimizedScore : undefined,
        optimizationId,
        applicationStatus: 'in_progress',
      });

      if (saved?.id) {
        returnNudge.arm({
          applicationId: saved.id,
          jobTitle,
          company:       companyName,
        });

        const bundleParts: string[] = ['PDF downloaded'];
        if (clipboardCopied) bundleParts.push('cover letter copied');
        bundleParts.push('tracked in My Activities');

        const message = portalOpened
          ? `Bundle ready for ${jobTitle} at ${companyName}: ${bundleParts.join(' · ')}. Confirm here once you're back from the portal.`
          : `Popup blocked for ${companyName}. ${bundleParts.join(' · ')}. Allow popups or open the listing manually, then mark as Applied.`;

        const user = await getCurrentUser();
        if (user?.id) {
          await pushUserNotification(user.id, {
            type: 'apply_handoff',
            entity_type: 'job_application',
            entity_id: saved.id,
            message,
          });
        }
      }

      await fetchOptimizations();
    } catch (err) {
      console.warn('[OptimizationHistory] handleAssistedApply error:', err);
    } finally {
      setApplyingOptId(null);
    }
  };

  /**
   * Quick-confirm path for `in_progress` rows: user clicks "Yes, applied"
   * or "Didn't apply" directly in the row instead of waiting for the
   * return-nudge popover. Flips the row to the final status.
   */
  const handleConfirmApply = async (applicationId: string, outcome: 'applied' | 'not_applied') => {
    setConfirmingAppId(applicationId);
    try {
      await updateApplicationStatus(
        applicationId,
        outcome,
        outcome === 'applied'
          ? 'Confirmed via My Activities quick-confirm.'
          : 'User reported they did not submit on the portal.',
      );
      await fetchOptimizations();
    } catch (err) {
      console.warn('[OptimizationHistory] handleConfirmApply error:', err);
    } finally {
      setConfirmingAppId(null);
    }
  };

  // Rating is based on the absolute optimised score, NOT on the delta.
  // The per-section pipeline's improvement estimate is artificial (+15 baseline), so
  // using the delta always produced "Excellent" regardless of actual quality.
  const getImprovementCategory = (_originalScore: number, optimizedScore: number) => {
    if (optimizedScore >= 80) return { label: t('dashboard.optimizationHistory.improvementExcellent'), color: 'text-[var(--vscode-success)]', bg: 'bg-[var(--vscode-success-bg)]', border: 'border-[var(--vscode-success-border)]' };
    if (optimizedScore >= 60) return { label: t('dashboard.optimizationHistory.improvementGood'), color: 'vscode-text-accent', bg: 'vscode-bg-tertiary/50', border: 'vscode-border' };
    if (optimizedScore >= 40) return { label: t('dashboard.optimizationHistory.improvementFair'), color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' };
    return { label: t('dashboard.optimizationHistory.improvementModerate'), color: 'vscode-text-secondary', bg: 'vscode-bg-tertiary/30', border: 'vscode-border' };
  };

  // Detect scraper placeholder values (returned when LinkedIn / Groq rate-limits block extraction)
  const PLACEHOLDER_TITLES = new Set(['position title', 'job title', 'job position', 'title', 'position']);
  const PLACEHOLDER_COMPANIES = new Set(['company name', 'company', 'employer', 'organization']);

  const isPlaceholder = (value: string | undefined, set: Set<string>) =>
    !value || set.has(value.trim().toLowerCase());

  /** Derive a display label from the job URL when the scraped title is a placeholder */
  const resolveJobTitle = (opt: any): string => {
    const raw = opt.jobTitle || opt.job_title || '';
    if (!isPlaceholder(raw, PLACEHOLDER_TITLES)) return raw;
    const url: string = opt.jobUrl || opt.job_url || '';
    if (url.includes('linkedin.com/jobs/view/')) {
      const m = url.match(/linkedin\.com\/jobs\/view\/(\d+)/);
      return m ? `${t('dashboard.optimizationHistory.linkedInJob')} #${m[1]}` : t('dashboard.optimizationHistory.linkedInJob');
    }
    if (url.includes('naukri.com')) return t('dashboard.optimizationHistory.naukriJob');
    if (url.includes('indeed.com')) return t('dashboard.optimizationHistory.indeedJob');
    if (url.includes('glassdoor.com')) return t('dashboard.optimizationHistory.glassdoorJob');
    if (url) return t('dashboard.optimizationHistory.jobPortalFallback');
    return '—';
  };

  const resolveCompany = (opt: any): string => {
    const raw = opt.companyName || opt.company_name || '';
    if (!isPlaceholder(raw, PLACEHOLDER_COMPANIES)) return raw;
    const url: string = opt.jobUrl || opt.job_url || '';
    if (url.includes('linkedin.com')) return t('dashboard.optimizationHistory.companyLinkedIn');
    if (url.includes('naukri.com')) return t('dashboard.optimizationHistory.companyNaukri');
    if (url.includes('indeed.com')) return t('dashboard.optimizationHistory.companyIndeed');
    if (url.includes('glassdoor.com')) return t('dashboard.optimizationHistory.companyGlassdoor');
    return '—';
  };

  // Filter and sort optimizations
  const filteredOptimizations = optimizations
    .filter(opt => {
      const matchesSearch = opt.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           opt.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (needsActionOnly) {
        const step = nextStepFor(getApplicationForOpt(opt));
        if (!step.needsAction) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'position':
          comparison = (a.jobTitle || '').localeCompare(b.jobTitle || '');
          break;
        case 'company':
          comparison = (a.companyName || '').localeCompare(b.companyName || '');
          break;
        case 'date':
          comparison = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          break;
        case 'category':
          const catA = getImprovementCategory(a.originalScore || 0, a.optimizedScore || 0).label;
          const catB = getImprovementCategory(b.originalScore || 0, b.optimizedScore || 0).label;
          comparison = catA.localeCompare(catB);
          break;
        default:
          return 0;
      }
      
      // For date: comparison = b - a (positive when b is newer). Desc = newest first = keep b before a = return comparison.
      // For others: desc = reverse of asc, so multiply by -1 when desc.
      if (sortBy === 'date') {
        return sortOrder === 'desc' ? comparison : -comparison;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  // Show all records — no blur or ghost rows (gate only at optimization start + AI Companion)
  const totalPages = Math.ceil(filteredOptimizations.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentOptimizations = filteredOptimizations.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    setCurrentPage(1); // Reset to first page
  };

  // Handle optimization selection
  const handleSelectOptimization = (optimization: any) => {
    setSelectedOptimization(optimization);
    setDrawerTab('resume');
    setShowResumeModal(true);
  };

  /**
   * Open the unified artifact drawer on a specific tab. Replaces the old
   * two-modal approach (one for resume, one for cover/app letters) with a
   * single right-side drawer that shows all three artifacts as tabs.
   */
  const openArtifactDrawer = (
    optimization: any,
    tab: 'resume' | 'cover' | 'application',
  ) => {
    setSelectedOptimization(optimization);
    setDrawerTab(tab);
    setShowResumeModal(true);
  };

  // Download handlers — `optimization` is passed directly to avoid stale-closure
  // bugs that arise from calling setState + handler synchronously.
  // Uses the exact same generateResumePDF / generateResumeDOCX path as the
  // DownloadStep in the optimization flow so output is identical.
  const DEFAULT_HISTORY_TEMPLATE = 'ats-optimized';

  /**
   * Download a saved resume in the requested format. Uses the SAME direct
   * generator path (`generateResumePDF` / `generateResumeDOCX`) that the
   * agent canvas uses, so a download triggered from the My Activities table
   * produces identical output to one triggered from the canvas.
   *
   * Previously this path used `captureResumePreviewAsPdf` (html2canvas) which
   * could silently fail when off-screen rendering hit a missing font / CSS
   * race condition — making My Activities downloads "do nothing" while canvas
   * downloads worked fine. Falling back to the direct generator removes that
   * inconsistency.
   *
   * If the in-memory row lacks `optimizedResume` (e.g. agent-synthetic rows
   * without `resume_version`), we fetch from `llm_optimization_levelX` by
   * `resume_version_id` before bailing out.
   */
  const handleDownload = async (format: 'pdf' | 'docx', optimization: any) => {
    let resumeText: string = optimization?.optimizedResume
      || optimization?.optimized_resume
      || optimization?.final_resume
      || '';

    // Fallback: fetch from optimization tables when the row was loaded without
    // resume content (some synthetic agent rows + older history rows).
    if (!resumeText) {
      const optId =
        optimization?.optimization_id ||
        (optimization?._agentApplication as { resume_version_id?: string })?.resume_version_id;
      if (optId && typeof optId === 'string' && !optId.startsWith('agent-')) {
        try {
          const { data: opt2 } = await supabase
            .from('llm_optimization_level2')
            .select('optimized_resume, final_resume')
            .eq('optimization_id', optId)
            .single();
          resumeText = (opt2?.optimized_resume || opt2?.final_resume || '') as string;
          if (!resumeText) {
            const { data: opt1 } = await supabase
              .from('llm_optimization_level1')
              .select('optimized_resume, final_resume')
              .eq('optimization_id', optId)
              .single();
            resumeText = (opt1?.optimized_resume || opt1?.final_resume || '') as string;
          }
        } catch (e) {
          console.warn('[OptimizationHistory] fallback fetch failed:', e);
        }
      }
    }

    if (!resumeText) {
      // Surface the failure so users aren't left wondering why nothing happens.
      window.alert('This activity has no saved resume content to download.');
      return;
    }

    try {
      const jobTitle = optimization?.jobTitle || optimization?.job_title || 'Job Position';
      const tplIdRaw: string = optimization?.selectedTemplateId ?? DEFAULT_HISTORY_TEMPLATE;
      const templateId = getTemplateSkinClass(tplIdRaw) as TemplateId;

      const normalizedText = normalizeResumeText(resumeText);

      // Prefer pre-built LLM sections when present, fallback to parsing.
      const rawLlmSections = optimization?.sections as Array<{ title: string; content: string }> | undefined;
      const docSections = Array.isArray(rawLlmSections) && rawLlmSections.length > 0
        ? sortSectionsByCanonicalOrder(
            normalizeSectionTitles(
              rawLlmSections
                .filter((s: { title?: string; content?: string }) => s?.title && s?.content?.trim())
                .map((s: { title: string; content: string }) => ({
                  title: s.title.trim(),
                  content: s.content.trim(),
                  preview: s.content.trim().slice(0, 120),
                }))
            )
          )
        : (() => {
            const parsed = parseResumeSections(normalizedText);
            return parsed.length > 0
              ? sortSectionsByCanonicalOrder(normalizeSectionTitles(parsed))
              : undefined;
          })();

      const fileName = `optimized-resume-${jobTitle.replace(/\s+/g, '-').toLowerCase()}.${format}`;
      const jTitle = '';

      // Use the SAME direct-generator path as the agent canvas for consistency.
      // Avoids the html2canvas race that caused some My Activities downloads
      // to silently no-op while canvas downloads worked fine.
      const blob = format === 'pdf'
        ? await generateResumePDF(normalizedText, docSections, jTitle, templateId)
        : await generateResumeDOCX(normalizedText, docSections, jTitle, templateId);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      window.alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
    }
  };

  if (loading) {
    if (Capacitor.isNativePlatform()) return null;
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`ggj-light-palette w-full min-w-0 p-3 sm:p-4 ${Capacitor.isNativePlatform() ? 'pt-3' : ''}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchOptimizations}
              className="px-4 py-2 min-h-[44px] rounded-lg transition-opacity hover:opacity-90 flex items-center space-x-2 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
              style={{ background: 'var(--brand-cta-bg)' }}
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (optimizations.length === 0) {
    return (
      <div className={`ggj-light-palette w-full min-w-0 p-3 sm:p-4 ${Capacitor.isNativePlatform() ? 'pt-3' : ''}`}>
        <div className="vscode-bg-secondary rounded-xl border vscode-border p-8 md:p-12 text-center max-w-md mx-auto">
          <History className="w-12 h-12 md:w-16 md:h-16 vscode-text-tertiary mx-auto mb-4" />
          <h3 className="text-lg md:text-xl font-semibold vscode-text-primary mb-2">No activities yet</h3>
          <p className="text-sm md:text-base vscode-text-tertiary mb-6">
            {t('dashboard.optimizationHistory.noActivitiesHint')}
          </p>
          <button
            type="button"
            onClick={() => onNavigate?.('agent')}
            className="px-6 py-3 min-h-[44px] font-semibold rounded-lg transition-opacity hover:opacity-90 touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
            style={{ background: 'var(--brand-cta-bg)' }}
          >
            {t('dashboard.optimizationHistory.startOptimizing', 'Start Optimizing')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-tour="activity-history-main"
      className="ggj-light-palette ds-activity-page flex flex-col w-full max-w-full min-w-0 overflow-x-hidden pb-[max(5rem,calc(4.5rem+env(safe-area-inset-bottom)))] md:pb-6"
      style={Capacitor.isNativePlatform() ? {} : { padding: '12px 16px 24px', gap: '0.75rem' }}
    >
      {/* ── NATIVE LAYOUT ── completely separate from web layout */}
      {Capacitor.isNativePlatform() ? (
        <div style={{ padding: '12px 12px 16px', paddingBottom: 'calc(16px + max(env(safe-area-inset-bottom), 8px))', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Stats pills row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--brand-accent-bg)', border: '1.5px solid var(--brand-accent-border)', borderRadius: 20, padding: '7px 16px' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--vscode-primary)', letterSpacing: '-0.02em' }}>{optimizations.length}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--vscode-primary)', opacity: 0.85 }}>{t('dashboard.optimizationHistory.total')}</span>
            </div>
            {(() => {
              const avg = Math.round(optimizations.reduce((sum, opt) => sum + (opt.optimizedScore - opt.originalScore), 0) / optimizations.length) || 0;
              return avg !== 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--vscode-success-bg)', border: '1.5px solid var(--vscode-success-border)', borderRadius: 20, padding: '7px 16px' }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--vscode-success)', letterSpacing: '-0.02em' }}>{avg > 0 ? '+' : ''}{avg}%</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--vscode-success)', opacity: 0.85 }}>{t('dashboard.optimizationHistory.avgImprovement')}</span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Search input */}
          <div style={{ position: 'relative' }}>
            <Search style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--vscode-text-tertiary)', pointerEvents: 'none' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('dashboard.optimizationHistory.searchPlaceholder')}
              aria-label={t('dashboard.optimizationHistory.searchAriaLabel')}
              style={{ width: '100%', paddingLeft: 40, paddingRight: 14, paddingTop: 12, paddingBottom: 12, borderRadius: 14, border: '1.5px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-input)', color: 'var(--vscode-text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box', boxShadow: 'var(--vscode-shadow-sm)' }}
            />
          </div>

          {/* Sort pills */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['date', 'position', 'company'] as const).map((s) => {
              const isActive = sortBy === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSortBy(s)}
                  style={{
                    flex: 1, minHeight: 38, borderRadius: 20,
                    border: `1.5px solid ${isActive ? 'var(--ggj-header-bg)' : 'var(--vscode-border-primary)'}`,
                    background: isActive ? 'var(--ggj-header-bg)' : 'var(--vscode-bg-elevated)',
                    color: isActive ? 'var(--ggj-header-text, #fff)' : 'var(--vscode-text-secondary)',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: 'none',
                  }}
                >
                  {s === 'date' ? t('dashboard.optimizationHistory.sortDate') : s === 'position' ? t('dashboard.optimizationHistory.sortPosition') : t('dashboard.optimizationHistory.sortCompany')}
                </button>
              );
            })}
          </div>

          {/* Card list — extra bottom padding so last card's buttons stay above tab bar when scrolled */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 24 }}>
            {currentOptimizations.map((optimization, index) => {
              const step = nextStepFor(getApplicationForOpt(optimization));
              return (
                <MobileHistoryCard
                  key={optimization.optimization_id || index}
                  jobTitle={resolveJobTitle(optimization)}
                  company={resolveCompany(optimization)}
                  date={optimization.created_at}
                  originalScore={optimization.originalScore || optimization.initial_score || 0}
                  optimizedScore={optimization.optimizedScore || optimization.match_score || 0}
                  applied={!step.needsAction && step.key !== 'needs_apply'}
                  locale={i18n.language}
                  onPreview={() => openArtifactDrawer(optimization, 'resume')}
                  onApply={step.key === 'needs_apply' ? () => { void handleAssistedApply(optimization); } : undefined}
                />
              );
            })}
          </div>

        </div>
      ) : (
      <div className="flex flex-col space-y-3">

      {/* Search and Filters */}
      <div className="vscode-bg-secondary rounded-xl border vscode-border p-3 flex-shrink-0">
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 vscode-text-tertiary pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('dashboard.optimizationHistory.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 vscode-input rounded-xl sm:rounded-lg vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] text-base touch-manipulation"
              aria-label={t('dashboard.optimizationHistory.searchAriaLabel')}
            />
          </div>
          {(() => {
            const needsActionCount = optimizations.filter((o) =>
              nextStepFor(getApplicationForOpt(o)).needsAction,
            ).length;
            return (
              <div className="flex gap-2 items-stretch">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 min-w-0 md:flex-none md:w-48 vscode-input rounded-xl sm:rounded-lg px-3 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 vscode-text-primary text-base focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation"
                  aria-label={t('dashboard.optimizationHistory.sortByAriaLabel')}
                >
                  <option value="date">{t('dashboard.optimizationHistory.sortDate')}</option>
                  <option value="position">{t('dashboard.optimizationHistory.sortPosition')}</option>
                  <option value="company">{t('dashboard.optimizationHistory.sortCompany')}</option>
                </select>
                <button
                  type="button"
                  onClick={() => { setNeedsActionOnly((v) => !v); setCurrentPage(1); }}
                  aria-pressed={needsActionOnly}
                  className="inline-flex shrink-0 items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-colors touch-manipulation min-h-[44px] max-w-[50%]"
                  style={needsActionOnly
                    ? { background: 'rgba(220,38,38,0.10)', borderColor: 'rgba(220,38,38,0.40)', color: '#b91c1c' }
                    : { background: 'transparent', borderColor: 'var(--vscode-border-primary, #e5e7eb)', color: 'var(--vscode-text-secondary)' }
                  }
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">Needs Action</span>
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full text-[10px] font-bold shrink-0"
                    style={needsActionOnly
                      ? { background: '#dc2626', color: '#fff' }
                      : { background: 'var(--vscode-bg-tertiary, #f3f4f6)', color: 'var(--vscode-text-tertiary)' }
                    }
                  >
                    {needsActionCount}
                  </span>
                </button>
                {needsActionOnly && (
                  <button
                    type="button"
                    onClick={() => setNeedsActionOnly(false)}
                    className="shrink-0 self-center text-xs vscode-text-tertiary hover:vscode-text-secondary underline underline-offset-2 min-h-[44px] px-1 touch-manipulation"
                  >
                    Clear
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Web card list + table */}
      <div className="ds-activity-table-shell vscode-bg-secondary rounded-xl overflow-hidden flex-shrink-0 flex flex-col min-w-0">

        {/* Mobile/tablet card list — clear hierarchy: job info, then primary action (Apply), then Preview + Download with labels */}
        <div className={`${Capacitor.isNativePlatform() ? 'hidden' : 'md:hidden'} flex flex-col divide-y vscode-border`}>
          {currentOptimizations.map((optimization, index) => {
            const category = getImprovementCategory(optimization.originalScore || optimization.initial_score || 0, optimization.optimizedScore || optimization.match_score || 0);
            const app = getApplicationForOpt(optimization);
            const coverText = app ? ((app as { cover_letter_snippet?: string }).cover_letter_snippet || app.cover_letter_version || '') : '';
            const appLetterText = app ? ((app as { application_snippet?: string }).application_snippet || app.application_notes || '') : '';
            const step = nextStepFor(app);
            const rowId = String(optimization.optimization_id ?? index);
            const jobDisplay = splitJobDisplay(resolveJobTitle(optimization));
            const isExpanded = expandedRowId === rowId;
            const jobUrl = resolveJobUrl(optimization, app);
            const dateLabel = new Date(optimization.created_at).toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric', year: '2-digit' });
            const documentMenuItems = buildDocumentMenuItems(coverText, appLetterText, onNavigate, {
              onPreviewCover: () => openArtifactDrawer(optimization, 'cover'),
              onPreviewApplication: () => openArtifactDrawer(optimization, 'application'),
              onGenerateCover: () => {
                try {
                  sessionStorage.setItem('history_apply_optimization_id', optimization.optimization_id);
                  if (optimization.job_id) sessionStorage.setItem('history_apply_job_id', optimization.job_id);
                  if (optimization.jobUrl) sessionStorage.setItem('history_apply_job_url', optimization.jobUrl);
                  sessionStorage.setItem('history_apply_job_title', optimization.jobTitle || '');
                  sessionStorage.setItem('history_apply_company', optimization.companyName || '');
                  sessionStorage.setItem('history_generate_letter_type', 'cover');
                } catch { /* ignore */ }
                onNavigate?.('agent');
              },
              onGenerateApplication: () => {
                try {
                  sessionStorage.setItem('history_apply_optimization_id', optimization.optimization_id);
                  if (optimization.job_id) sessionStorage.setItem('history_apply_job_id', optimization.job_id);
                  if (optimization.jobUrl) sessionStorage.setItem('history_apply_job_url', optimization.jobUrl);
                  sessionStorage.setItem('history_apply_job_title', optimization.jobTitle || '');
                  sessionStorage.setItem('history_apply_company', optimization.companyName || '');
                  sessionStorage.setItem('history_generate_letter_type', 'application');
                } catch { /* ignore */ }
                onNavigate?.('agent');
              },
            });
            const stepMenuItems = buildStepHoverItems(step, jobUrl, {
              onOpenPortal: () => { void handleAssistedApply(optimization); },
              onConfirmNotApplied: () => { if (app?.id) void handleConfirmApply(app.id, 'not_applied'); },
              onReopenBundle: () => { void handleAssistedApply(optimization); },
            });
            return (
              <motion.div
                key={rowId}
                className="ds-activity-mobile-card relative px-3 py-3 vscode-bg-primary/20 transition-colors border-b vscode-border last:border-b-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                {/* Condensed header: title + match score; metadata inline */}
                <MobileActivityCardHeader
                  display={jobDisplay}
                  company={resolveCompany(optimization)}
                  location={optimization.location || t('dashboard.optimizationHistory.remote')}
                  dateLabel={dateLabel}
                  score={optimization.optimizedScore || optimization.match_score || 0}
                  scoreTitle={category.label}
                  expanded={isExpanded}
                  onToggleExpand={() => setExpandedRowId(isExpanded ? null : rowId)}
                />
                {isExpanded && jobDisplay.extra && (
                  <div
                    className="mt-2 rounded-lg border px-3 py-2 text-[11px] leading-relaxed"
                    style={{
                      borderColor: 'var(--ds-border, #e5e7eb)',
                      background: 'var(--ds-bg-recessed, #f8fafc)',
                      color: 'var(--ds-text-body, #475569)',
                    }}
                  >
                    {jobDisplay.full}
                  </div>
                )}

                {/* Primary action + overflow menu (documents live in ⋮ menu) */}
                <div className="ds-activity-mobile-card__status mt-2 pt-2 border-t vscode-border/60 flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                  <NextStepCell
                    step={step}
                    sendingReminder={sendingReminderId === app?.id}
                    applying={applyingOptId === (optimization.optimization_id ?? optimization.job_id ?? optimization.jobTitle)}
                    confirming={!!app?.id && confirmingAppId === app.id}
                    onApply={() => { void handleAssistedApply(optimization); }}
                    onConfirm={(outcome) => {
                      if (app?.id) void handleConfirmApply(app.id, outcome);
                    }}
                    onFollowUp={() => {
                      if (app?.id) void sendReminderEmail(app.id);
                    }}
                    onPrep={() => {
                      if (!onNavigate) return;
                      try {
                        sessionStorage.setItem('prep_job_title', optimization.jobTitle || '');
                        sessionStorage.setItem('prep_company', optimization.companyName || '');
                      } catch { /* ignore */ }
                      onNavigate('agent');
                    }}
                  />
                  </div>
                  <RowHoverMenu
                    jobUrl={jobUrl}
                    onOpenPortal={() => { void handleAssistedApply(optimization); }}
                    onPreview={() => openArtifactDrawer(optimization, 'resume')}
                    previewLabel={t('dashboard.optimizationHistory.previewResume')}
                    secondaryItems={[...documentMenuItems, ...stepMenuItems]}
                    alwaysVisibleOnTouch
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Desktop: table — mirrors PrepareForInterview styling exactly */}
        <div className="hidden md:block overflow-x-auto">
          <table className="ds-activity-table w-full text-left border-collapse">
            <thead>
              <tr className="ds-activity-table__head-row vscode-bg-primary/50 text-xs font-extrabold vscode-text-primary uppercase tracking-wide">
                <th className="ds-activity-table__th w-10">#</th>
                <th className="ds-activity-table__th">
                  <TableSortButton label={t('dashboard.optimizationHistory.jobDetails')} active={sortBy === 'position'} order={sortOrder} onClick={() => handleSort('position')} />
                </th>
                <th className="ds-activity-table__th whitespace-nowrap">
                  <TableSortButton label={t('dashboard.optimizationHistory.dateColumn')} active={sortBy === 'date'} order={sortOrder} onClick={() => handleSort('date')} />
                </th>
                <th className="ds-activity-table__th whitespace-nowrap">
                  <TableSortButton label="Match" active={sortBy === 'category'} order={sortOrder} onClick={() => handleSort('category')} />
                </th>
                <th className="ds-activity-table__th whitespace-nowrap">Docs</th>
                <th className="ds-activity-table__th whitespace-nowrap">Status</th>
                <th className="ds-activity-table__th w-10 text-center">{t('dashboard.support.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {currentOptimizations.map((optimization, index) => {
                const absoluteIndex = startIndex + index;
                const category = getImprovementCategory(
                  optimization.originalScore || optimization.initial_score || 0,
                  optimization.optimizedScore || optimization.match_score || 0,
                );
                const serialNumber = absoluteIndex + 1;
                const app = getApplicationForOpt(optimization);
                const coverText = app
                  ? ((app as { cover_letter_snippet?: string }).cover_letter_snippet || app.cover_letter_version || '')
                  : '';
                const appLetterText = app
                  ? ((app as { application_snippet?: string }).application_snippet || app.application_notes || '')
                  : '';
                const rowId = String(optimization.optimization_id ?? index);
                const jobDisplay = splitJobDisplay(resolveJobTitle(optimization));
                const isExpanded = expandedRowId === rowId;
                const jobUrl = resolveJobUrl(optimization, app);
                const step = nextStepFor(app);
                const rowActions = buildStepHoverItems(step, jobUrl, {
                  onOpenPortal: () => { void handleAssistedApply(optimization); },
                  onConfirmNotApplied: () => { if (app?.id) void handleConfirmApply(app.id, 'not_applied'); },
                  onReopenBundle: () => { void handleAssistedApply(optimization); },
                });
                const openListing = () => {
                  if (jobUrl) window.open(jobUrl, '_blank', 'noopener,noreferrer');
                  else void handleAssistedApply(optimization);
                };
                const jobTitle = resolveJobTitle(optimization);

                return (
                  <React.Fragment key={rowId}>
                    <tr className={`ds-activity-table__row group transition-colors ${step.needsAction ? 'border-l-2 border-l-[var(--vscode-primary)]' : ''}`}>

                      {/* # */}
                      <td className="ds-activity-table__td text-sm vscode-text-secondary font-medium">{serialNumber}</td>

                      {/* Job — title + company + location stacked */}
                      <td className="ds-activity-table__td">
                        <div className="flex items-start gap-1 min-w-0">
                          {jobDisplay.extra && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setExpandedRowId(isExpanded ? null : rowId); }}
                              className="mt-0.5 shrink-0 p-0.5 rounded vscode-text-tertiary hover:vscode-text-secondary"
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Collapse' : 'Expand full title'}
                            >
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                          <div className="min-w-0">
                            <p className="t-body truncate max-w-[220px]" title={jobDisplay.full}>{jobDisplay.short}</p>
                            <p className="t-meta mt-px truncate max-w-[220px]">
                              {resolveCompany(optimization) || '—'}
                              {optimization.location ? ` · ${optimization.location}` : ` · ${t('dashboard.optimizationHistory.remote')}`}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="ds-activity-table__td t-meta whitespace-nowrap">
                        {new Date(optimization.created_at).toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric', year: '2-digit' })}
                      </td>

                      {/* Match */}
                      <td className="ds-activity-table__td">
                        <MatchScoreRing score={optimization.optimizedScore || optimization.match_score || 0} title={category.label} />
                      </td>

                      {/* Docs — two icon buttons */}
                      <td className="ds-activity-table__td">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title={coverText ? 'Review cover letter' : 'Add cover letter'}
                            aria-label={coverText ? 'Review cover letter' : 'Add cover letter'}
                            onClick={(e) => { e.stopPropagation(); if (coverText) openArtifactDrawer(optimization, 'cover'); else if (onNavigate) navigateToGenerateLetter(optimization, 'cover', onNavigate); }}
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors ${coverText ? 'vscode-text-primary vscode-border vscode-bg-primary/30 hover:vscode-bg-hover' : 'vscode-text-tertiary border-dashed vscode-border hover:vscode-bg-hover'}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            title={appLetterText ? 'Review application note' : 'Add application note'}
                            aria-label={appLetterText ? 'Review application note' : 'Add application note'}
                            onClick={(e) => { e.stopPropagation(); if (appLetterText) openArtifactDrawer(optimization, 'application'); else if (onNavigate) navigateToGenerateLetter(optimization, 'application', onNavigate); }}
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition-colors ${appLetterText ? 'vscode-text-primary vscode-border vscode-bg-primary/30 hover:vscode-bg-hover' : 'vscode-text-tertiary border-dashed vscode-border hover:vscode-bg-hover'}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="ds-activity-table__td">
                        <NextStepCell
                          step={step}
                          sendingReminder={sendingReminderId === app?.id}
                          applying={applyingOptId === (optimization.optimization_id ?? optimization.job_id ?? optimization.jobTitle)}
                          confirming={!!app?.id && confirmingAppId === app.id}
                          onApply={() => { void handleAssistedApply(optimization); }}
                          onConfirm={(outcome) => { if (app?.id) void handleConfirmApply(app.id, outcome); }}
                          onFollowUp={() => { if (app?.id) void sendReminderEmail(app.id); }}
                          onPrep={() => {
                            if (!onNavigate) return;
                            try {
                              sessionStorage.setItem('prep_job_title', optimization.jobTitle || '');
                              sessionStorage.setItem('prep_company', optimization.companyName || '');
                            } catch { /* ignore */ }
                            onNavigate('agent');
                          }}
                        />
                      </td>

                      {/* Actions — single ⋮ button, all options in dropdown */}
                      <td className="ds-activity-table__td text-center">
                        <RowActionsMenu
                          jobTitle={jobTitle}
                          onPreview={() => openArtifactDrawer(optimization, 'resume')}
                          onPortal={openListing}
                          extraItems={rowActions}
                        />
                      </td>
                    </tr>

                    {/* Expanded full title */}
                    {isExpanded && jobDisplay.extra && (
                      <tr className="vscode-bg-primary/20">
                        <td colSpan={7} className="px-4 pb-3 pt-1">
                          <div className="ml-8 rounded-lg border vscode-border px-3 py-2 text-xs vscode-text-secondary leading-relaxed vscode-bg-primary/40">
                            {jobDisplay.full}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination — mirrors PrepareForInterview */}
        <div className="ds-activity-table__pagination px-3 sm:px-4 py-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 vscode-bg-primary/30">
          <span className="text-sm vscode-text-secondary order-2 sm:order-1">
            {t('dashboard.optimizationHistory.showing', {
              from: Math.min(startIndex + 1, filteredOptimizations.length),
              to: Math.min(endIndex, filteredOptimizations.length),
              total: filteredOptimizations.length,
            })}
          </span>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 order-1 sm:order-2">
            <label className="flex items-center gap-2 text-sm vscode-text-secondary">
              <span className="whitespace-nowrap">{t('dashboard.optimizationHistory.itemsPerPage')}:</span>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                className="py-2.5 sm:py-1.5 px-3 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm min-w-[4rem] min-h-[44px] sm:min-h-0 touch-manipulation"
                aria-label={t('dashboard.optimizationHistory.itemsPerPage')}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="min-h-[44px] min-w-[44px] sm:min-w-0 py-2.5 sm:py-1.5 px-4 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                aria-label={t('dashboard.optimizationHistory.previousPage')}
              >
                {t('dashboard.optimizationHistory.prev')}
              </button>
              <span className="text-sm vscode-text-tertiary px-2 min-w-[6rem] text-center py-2">
                {t('dashboard.optimizationHistory.pageOf', { current: currentPage, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="min-h-[44px] min-w-[44px] sm:min-w-0 py-2.5 sm:py-1.5 px-4 rounded-xl sm:rounded border vscode-border vscode-bg-primary vscode-text-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                aria-label={t('dashboard.optimizationHistory.nextPage')}
              >
                {t('dashboard.optimizationHistory.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      )}

      {/* Cover letter / application letter previews now live in the unified
          artifact drawer below — clicking the cover / app letter buttons in a
          row calls openArtifactDrawer(opt, 'cover'|'application') which sets
          drawerTab and opens the right-side panel on the matching tab. */}

      {/* Artifact Drawer — slide-in panel from the right with tabs for
          Resume / Cover Letter / Application Note. Replaces the centered
          popup so users can see the full A4 resume side-by-side with the
          rest of the page. */}
      {showResumeModal && selectedOptimization && createPortal(
        (() => {
          const opt = selectedOptimization;
          const app = getApplicationForOpt(opt);
          const coverText: string = app
            ? ((app as { cover_letter_snippet?: string }).cover_letter_snippet || app.cover_letter_version || '')
            : '';
          const appLetterText: string = app
            ? ((app as { application_snippet?: string }).application_snippet || app.application_notes || '')
            : '';
          const resumeRaw: string =
            opt.optimizedResume || (opt as any).optimized_resume || (opt as any).final_resume || '';

          const closeDrawer = () => {
            setShowResumeModal(false);
            setResumeCopied(false);
            setDrawerCopied(false);
          };

          const activeTabContent =
            drawerTab === 'resume' ? resumeRaw
            : drawerTab === 'cover' ? coverText
            : appLetterText;

          const copyActiveTabContent = () => {
            if (!activeTabContent) return;
            void navigator.clipboard.writeText(activeTabContent).then(() => {
              setDrawerCopied(true);
              window.setTimeout(() => setDrawerCopied(false), 2000);
            });
          };

          const drawerToolBtn =
            'ds-artifact-drawer__tool-btn min-h-[44px] min-w-[44px] p-0 flex items-center justify-center rounded-lg touch-manipulation shrink-0 transition-colors';

          const tabs: Array<{ id: 'resume' | 'cover' | 'application'; label: string; shortLabel: string; has: boolean }> = [
            { id: 'resume',      label: 'Resume',           shortLabel: 'Resume',   has: !!resumeRaw },
            { id: 'cover',       label: 'Cover Letter',     shortLabel: 'Cover',    has: !!coverText },
            { id: 'application', label: 'Application Note', shortLabel: 'App Note', has: !!appLetterText },
          ];

          return (
            <>
              <div
                className="ds-artifact-drawer__backdrop fixed inset-0 z-[9998]"
                onClick={closeDrawer}
                aria-hidden="true"
              />
              <aside
                className="ds-artifact-drawer fixed inset-0 md:inset-auto md:top-0 md:right-0 z-[9999] h-full flex flex-col shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="artifact-drawer-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ds-artifact-drawer__header flex items-center gap-2 px-3 md:px-4 shrink-0">
                  <div className="flex-1 min-w-0">
                    <h2
                      id="artifact-drawer-title"
                      className="ds-artifact-drawer__title truncate leading-snug"
                    >
                      <span className="md:hidden">{resolveJobTitle(opt)}</span>
                      <span className="hidden md:inline">
                        {resolveJobTitle(opt)}
                        {resolveCompany(opt) !== '—' ? ` · ${resolveCompany(opt)}` : ''}
                      </span>
                    </h2>
                    {resolveCompany(opt) !== '—' && (
                      <p className="ds-artifact-drawer__subtitle truncate md:hidden">
                        {resolveCompany(opt)}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="ds-artifact-drawer__close min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md touch-manipulation shrink-0"
                    aria-label="Close drawer (Esc)"
                    title="Close (Esc)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="ds-artifact-drawer__tabbar flex flex-col md:flex-row md:items-center gap-0 md:gap-2 px-2 md:px-3 pt-1 border-b shrink-0 overflow-hidden min-w-0">
                  <div className="ds-artifact-drawer__tabbar-tabs flex items-center gap-0.5 min-w-0 flex-1">
                    {tabs.map((tab) => {
                      const isActive = drawerTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => { setDrawerTab(tab.id); setDrawerCopied(false); }}
                          className={[
                            'ds-artifact-drawer__tab flex flex-1 md:flex-none items-center justify-center gap-1 px-2 md:px-3 py-2 min-h-[44px] min-w-0 touch-manipulation transition-colors',
                            isActive ? 'is-active' : '',
                            tab.has ? '' : 'is-empty',
                          ].filter(Boolean).join(' ')}
                        >
                          <span className="md:hidden">{tab.shortLabel}</span>
                          <span className="hidden md:inline">{tab.label}</span>
                          {!tab.has && <span className="ds-artifact-drawer__tab-empty">(empty)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {(activeTabContent || (drawerTab === 'resume' && resumeRaw)) && (
                    <div className="ds-artifact-drawer__tabbar-tools flex items-center justify-end gap-1.5 shrink-0 w-full md:w-auto px-1 py-2 md:py-0 border-t md:border-t-0 border-[var(--ds-divider)]">
                    {activeTabContent && (
                      <button
                        type="button"
                        onClick={copyActiveTabContent}
                        className={drawerToolBtn}
                        title={drawerCopied ? 'Copied!' : 'Copy to clipboard'}
                        aria-label={drawerCopied ? 'Copied' : 'Copy to clipboard'}
                      >
                        {drawerCopied
                          ? <Check className="w-4 h-4 text-emerald-500" />
                          : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                    {drawerTab === 'resume' && resumeRaw && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleDownload('pdf', opt)}
                          className={drawerToolBtn}
                          title={t('dashboard.optimizationHistory.downloadPdf')}
                          aria-label={t('dashboard.optimizationHistory.downloadPdf')}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload('docx', opt)}
                          className={drawerToolBtn}
                          title={t('dashboard.optimizationHistory.downloadDocx')}
                          aria-label={t('dashboard.optimizationHistory.downloadDocx')}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    </div>
                  )}
                </div>

                <div
                  className={[
                    'ds-artifact-drawer__body flex-1 min-h-0 overflow-y-auto',
                    drawerTab === 'resume' ? 'ds-artifact-drawer__body--resume' : 'ds-artifact-drawer__body--letter',
                  ].join(' ')}
                  style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                >
                  {drawerTab === 'resume' && (
                    !resumeRaw ? (
                      <EmptyTabState label="No optimized resume saved for this job." />
                    ) : (() => {
                      const savedTplId: string = (opt as any).selectedTemplateId || 'ats-optimized';
                      const savedDesign: { font?: string; color?: string; caps?: string } =
                        (opt as any).selectedDesign || {};
                      const designFont = savedDesign.font || 'default';
                      const designColor = savedDesign.color || '';
                      const designCaps = savedDesign.caps || 'none';

                      const rawSections = (opt as any).sections as Array<{ title: string; content: string }> | undefined;
                      const previewSections = Array.isArray(rawSections) && rawSections.length > 0
                        ? sortSectionsByCanonicalOrder(normalizeSectionTitles(
                            rawSections
                              .filter((s: any) => s?.title && s?.content?.trim())
                              .map((s: any) => ({ title: s.title.trim(), content: s.content.trim(), preview: s.content.trim().slice(0, 120) }))
                          ))
                        : sortSectionsByCanonicalOrder(normalizeSectionTitles(parseResumeSections(normalizeResumeText(resumeRaw))));

                      const ACCENT_COLORS = [
                        { swatch: '#94a3b8', accent: '#94a3b8', accentBg: '#f1f5f9', accentText: '#334155', accentBorder: '#cbd5e1' },
                        { swatch: '#9ca3af', accent: '#9ca3af', accentBg: '#f3f4f6', accentText: '#374151', accentBorder: '#d1d5db' },
                        { swatch: '#a8a29e', accent: '#a8a29e', accentBg: '#f5f5f4', accentText: '#44403c', accentBorder: '#d6d3d1' },
                        { swatch: '#86b886', accent: '#6aaa6a', accentBg: '#f0f7f0', accentText: '#2d5a2d', accentBorder: '#b8d8b8' },
                        { swatch: '#a78bfa', accent: '#a78bfa', accentBg: '#f5f3ff', accentText: '#5b21b6', accentBorder: '#ddd6fe' },
                        { swatch: '#fda4af', accent: '#fda4af', accentBg: '#fff1f2', accentText: '#9f1239', accentBorder: '#fecdd3' },
                      ];
                      const colorEntry = ACCENT_COLORS.find(c => c.swatch === designColor);

                      return (
                        <div className="ds-artifact-drawer__resume-stage">
                          <div className="ds-artifact-drawer__paper-wrap resume-preview-host--print">
                            <div
                              className={[
                                designColor ? 'resume-preview--custom-accent' : '',
                                designFont === 'serif'   ? 'resume-preview--font-serif'   : '',
                                designFont === 'compact' ? 'resume-preview--font-compact' : '',
                                `resume-preview--caps-${designCaps}`,
                              ].filter(Boolean).join(' ') || undefined}
                              style={colorEntry ? {
                                '--resume-accent': colorEntry.accent,
                                '--resume-accent-bg': colorEntry.accentBg,
                                '--resume-accent-text': colorEntry.accentText,
                                '--resume-accent-border': colorEntry.accentBorder,
                              } as React.CSSProperties : undefined}
                            >
                              <ResumePreviewForPDF
                                sections={previewSections}
                                templateId={savedTplId}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  )}

                  {drawerTab === 'cover' && (
                    !coverText
                      ? <EmptyTabState label="No cover letter saved for this job yet." />
                      : (
                        <div className="ds-artifact-drawer__letter-stage">
                          <div className="ds-artifact-drawer__letter t-body">
                            {formatLetterForDisplay(
                              `${opt.jobTitle || 'Job'} – ${t('dashboard.optimizationHistory.coverLetter')}`,
                              coverText,
                              {
                                letterLabel: t('dashboard.optimizationHistory.coverLetter'),
                                salutation: t('dashboard.optimizationHistory.letterSalutation'),
                                closing: t('dashboard.optimizationHistory.letterClosing'),
                                dateStr: new Date().toLocaleDateString(i18n.language || 'en', { dateStyle: 'long' }),
                              },
                            )}
                          </div>
                        </div>
                      )
                  )}

                  {drawerTab === 'application' && (
                    !appLetterText
                      ? <EmptyTabState label="No application note saved for this job yet." />
                      : (
                        <div className="ds-artifact-drawer__letter-stage">
                          <div className="ds-artifact-drawer__letter t-body">
                            {formatLetterForDisplay(
                              `${opt.jobTitle || 'Job'} – ${t('dashboard.optimizationHistory.applicationLetter')}`,
                              appLetterText,
                              {
                                letterLabel: t('dashboard.optimizationHistory.applicationLetter'),
                                salutation: t('dashboard.optimizationHistory.letterSalutation'),
                                closing: t('dashboard.optimizationHistory.letterClosing'),
                                dateStr: new Date().toLocaleDateString(i18n.language || 'en', { dateStyle: 'long' }),
                              },
                            )}
                          </div>
                        </div>
                      )
                  )}
                </div>
              </aside>
            </>
          );
        })(),
        document.body
      )}

      {/* Upgrade prompt modal */}
      {upgradePrompt && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setUpgradePrompt(null)}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="px-5 py-4 flex items-center gap-3"
              style={{ background: 'var(--ggj-header-bg)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <Zap className="w-5 h-5" style={{ color: 'var(--ggj-header-text)' }} />
              </div>
              <h3 className="font-bold text-base leading-tight" style={{ color: 'var(--ggj-header-text)' }}>{upgradePrompt.title}</h3>
            </div>

            {/* Body */}
            <div className="px-5 py-5 space-y-4">
              <p className="text-sm vscode-text-secondary leading-relaxed">{upgradePrompt.message}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setUpgradePrompt(null)}
                  className="flex-1 min-h-[42px] rounded-xl border vscode-border vscode-text-secondary text-sm font-medium hover:opacity-80 transition-opacity"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUpgradePrompt(null);
                    window.dispatchEvent(new CustomEvent('navigate-to-pricing'));
                  }}
                  className="flex-1 min-h-[42px] rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  <Zap className="w-4 h-4" /> Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Assisted Handoff return-nudge ──────────────────────────────
          Surfaces a "Did you finish applying?" prompt after the user
          comes back from the portal (≥30s dwell). Same hook as the
          Agent panel — but we render it here too so users who never
          leave My Activities still get the confirmation prompt.
          ──────────────────────────────────────────────────────────── */}
      {returnNudge.pending && createPortal(
        <div
          className="fixed bottom-6 right-6 z-[10000] max-w-sm rounded-xl shadow-2xl border animate-in fade-in slide-in-from-bottom-3"
          role="dialog"
          aria-label="Confirm application status"
          style={{
            background: '#ffffff',
            borderColor: 'var(--ds-border, #e5e7eb)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.06)',
          }}
        >
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(99,102,241,0.12)', color: '#4f46e5' }}
                aria-hidden
              >
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 leading-snug">
                  Did you finish applying?
                </div>
                <div className="text-xs text-gray-600 mt-0.5 leading-snug">
                  <span className="font-medium text-gray-800">{returnNudge.pending.jobTitle}</span>
                  <span className="text-gray-500"> @ {returnNudge.pending.company}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => returnNudge.dismiss()}
                className="text-gray-400 hover:text-gray-600 text-base leading-none shrink-0 -mt-0.5"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
          <div className="px-4 pb-4 pt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { void returnNudge.resolve('applied').then(() => fetchOptimizations()); }}
              className="flex-1 min-w-[110px] px-3 py-2 rounded-lg text-xs font-semibold text-white transition-colors"
              style={{ background: '#15803d' }}
            >
              Yes, applied
            </button>
            <button
              type="button"
              onClick={() => { void returnNudge.resolve('later'); }}
              className="flex-1 min-w-[90px] px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: 'rgba(99,102,241,0.10)', color: '#4f46e5' }}
            >
              Not yet
            </button>
            <button
              type="button"
              onClick={() => { void returnNudge.resolve('not_applied').then(() => fetchOptimizations()); }}
              className="flex-1 min-w-[110px] px-3 py-2 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}
            >
              Didn't apply
            </button>
          </div>
        </div>,
        document.body,
      )}

    </div>
  );
};

const TableSortButton: React.FC<{
  label: string;
  active: boolean;
  order: 'asc' | 'desc';
  onClick: () => void;
  className?: string;
}> = ({ label, active, order, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1 hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vscode-primary)] rounded ${className}`}
  >
    <span>{label}</span>
    {active && (order === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
  </button>
);



function navigateToGenerateLetter(
  optimization: { optimization_id?: string; job_id?: string; jobUrl?: string; jobTitle?: string; companyName?: string },
  letterType: 'cover' | 'application',
  onNavigate: (page: string) => void,
): void {
  try {
    sessionStorage.setItem('history_apply_optimization_id', optimization.optimization_id ?? '');
    if (optimization.job_id) sessionStorage.setItem('history_apply_job_id', optimization.job_id);
    if (optimization.jobUrl) sessionStorage.setItem('history_apply_job_url', optimization.jobUrl);
    sessionStorage.setItem('history_apply_job_title', optimization.jobTitle || '');
    sessionStorage.setItem('history_apply_company', optimization.companyName || '');
    sessionStorage.setItem('history_generate_letter_type', letterType);
  } catch { /* ignore */ }
  onNavigate('agent');
}

const MATCH_RING_COLORS: Record<string, string> = {
  excellent: '#10b981',
  good: '#6366f1',
  fair: '#38bdf8',
  moderate: '#94a3b8',
};

function matchRingTier(score: number): keyof typeof MATCH_RING_COLORS {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'moderate';
}

const MatchScoreRing: React.FC<{ score: number; title: string }> = ({ score, title }) => {
  const pct = Math.min(100, Math.max(0, Math.round(score)));
  const tier = matchRingTier(pct);
  const color = MATCH_RING_COLORS[tier];
  const isGoodMatch = pct >= 60;
  const isStrongMatch = pct >= 70;
  const size = 28;
  const r = 10;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const cx = size / 2;
  const arcStroke = isGoodMatch ? 3 : 2.5;
  return (
    <span className="inline-flex shrink-0" title={`${title} · ${pct}%`}>
      <span
        className={[
          'ds-activity-match-ring relative inline-flex',
          isGoodMatch ? 'ds-activity-match-ring--good' : '',
          isStrongMatch ? 'ds-activity-match-ring--strong' : '',
        ].filter(Boolean).join(' ')}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className="shrink-0 -rotate-90" aria-hidden>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(148,163,184,0.2)" strokeWidth={arcStroke} />
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={arcStroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-[9px] font-bold tabular-nums"
          style={{ color }}
        >
          {pct}
        </span>
      </span>
    </span>
  );
};

const StatusPill: React.FC<{ step: NextStepInfo; icon?: React.ReactNode }> = ({ step, icon }) => {
  const statusIcon = icon ?? stepStatusIcon(step);
  return (
    <span className={`${stepStatusClass(step.tone)} shrink-0`}>
      {step.pulse && (
        <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden title="Agent monitoring">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-50 bg-current" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      )}
      {statusIcon}
      {step.label}
    </span>
  );
};

const MobileActivityCardHeader: React.FC<{
  display: { short: string; extra: string | null; full: string };
  company: string;
  location: string;
  dateLabel: string;
  score: number;
  scoreTitle: string;
  expanded: boolean;
  onToggleExpand: () => void;
}> = ({ display, company, location, dateLabel, score, scoreTitle, expanded, onToggleExpand }) => {
  const metaParts = [company, location, dateLabel].filter(Boolean);
  return (
    <div className="ds-activity-mobile-card__header flex items-start justify-between gap-2 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1 min-w-0">
          {display.extra && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
              className="mt-0.5 shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center rounded hover:vscode-bg-hover touch-manipulation"
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse job details' : 'Expand full job title'}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform vscode-text-tertiary ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
          <p className="t-body font-semibold truncate leading-snug" title={display.full}>{display.short}</p>
        </div>
        <p className="t-meta truncate mt-0.5 leading-snug" title={metaParts.join(' · ')}>
          {metaParts.join(' · ')}
        </p>
      </div>
      <MatchScoreRing score={score} title={scoreTitle} />
    </div>
  );
};

const JobDetailsCell: React.FC<{
  display: { short: string; extra: string | null; full: string };
  company: string;
  location: string;
  expanded: boolean;
  onToggleExpand: () => void;
}> = ({ display, company, location, expanded, onToggleExpand }) => (
  <div className="min-w-0">
        <div className="flex items-start gap-1 min-w-0">
      {display.extra && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          className="mt-0.5 shrink-0 p-0.5 rounded hover:vscode-bg-hover"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse job details' : 'Expand full job title'}
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform vscode-text-tertiary ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <p className="t-body truncate" title={display.full}>{display.short}</p>
        <p className="t-meta truncate mt-px">{company}{company && location ? ' · ' : ''}{location}</p>
      </div>
    </div>
  </div>
);

/** One interactive pill — status label + action merged (no stacked controls). */
const InteractiveStepPill: React.FC<{
  onClick: () => void;
  title: string;
  disabled?: boolean;
  loading?: boolean;
  tone: StepTone;
  icon: React.ReactNode;
  label?: string;
  pulse?: boolean;
  iconOnly?: boolean;
}> = ({ onClick, title, disabled, loading, tone, icon, label, pulse, iconOnly }) => (
    <button
      type="button"
      disabled={disabled || loading}
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={[
        stepStatusClass(tone),
        'shrink-0 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
        iconOnly ? 'ds-activity-status--icon-only' : '',
      ].filter(Boolean).join(' ')}
    >
      {pulse && (
        <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-50 bg-current" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      )}
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : icon}
      {!iconOnly && label ? <span>{label}</span> : null}
    </button>
  );

const RowHoverMenu: React.FC<{
  jobUrl?: string;
  onOpenPortal: () => void;
  onPreview: () => void;
  previewLabel: string;
  secondaryItems: HoverMenuItem[];
  /** On touch layouts, keep actions reachable without hover */
  alwaysVisibleOnTouch?: boolean;
}> = ({ jobUrl, onOpenPortal, onPreview, previewLabel, secondaryItems, alwaysVisibleOnTouch }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openListing = () => {
    if (jobUrl) window.open(jobUrl, '_blank', 'noopener,noreferrer');
    else onOpenPortal();
  };

  const portalTitle = jobUrl ? 'Open job listing' : 'Open apply bundle';
  const visibility = alwaysVisibleOnTouch
    ? 'opacity-100'
    : 'opacity-[0.38] group-hover:opacity-100 group-focus-within:opacity-100';

  return (
    <div
      ref={ref}
      className={`relative inline-flex items-center rounded-lg border p-0.5 transition-all duration-200 ${visibility}`}
      style={{ borderColor: 'var(--ds-border, #e5e7eb)', background: 'var(--ds-bg-card, #fff)' }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="ds-activity-row-menu-btn min-h-[44px] min-w-[44px] h-11 w-11 inline-flex items-center justify-center rounded-md hover:bg-[var(--ds-bg-tinted)] touch-manipulation"
        aria-label="Row actions"
        aria-expanded={open}
        title="Resume, portal & more"
      >
        <MoreVertical className="w-4 h-4" style={{ color: 'var(--ds-text-muted)' }} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 min-w-[172px] rounded-lg border py-1 shadow-xl"
          style={{ background: 'var(--ds-bg-card, #fff)', borderColor: 'var(--ds-border, #e5e7eb)' }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); onPreview(); }}
            className="w-full px-3 py-2 text-left text-[11px] font-medium hover:bg-[var(--ds-bg-tinted)] flex items-center gap-2"
            style={{ color: 'var(--ds-text-strong)' }}
          >
            <Eye className="w-3.5 h-3.5 shrink-0" />
            {previewLabel}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); openListing(); }}
            className="w-full px-3 py-2 text-left text-[11px] font-medium hover:bg-[var(--ds-bg-tinted)] flex items-center gap-2"
            style={{ color: 'var(--ds-text-strong)' }}
          >
            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            {portalTitle}
          </button>
          {secondaryItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                item.onClick();
              }}
              className="w-full px-3 py-2 text-left text-[11px] font-medium hover:bg-[var(--ds-bg-tinted)]"
              style={{ color: item.danger ? '#dc2626' : 'var(--ds-text-strong)' }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface NextStepCellProps {
  step:            NextStepInfo;
  sendingReminder: boolean;
  applying:        boolean;
  confirming:      boolean;
  onApply:         () => void;
  onConfirm:       (outcome: 'applied' | 'not_applied') => void;
  onFollowUp:      () => void;
  onPrep:          () => void;
}

/** One control per row — passive status pill OR merged action pill. */
const NextStepCell: React.FC<NextStepCellProps> = ({
  step,
  sendingReminder,
  applying,
  confirming,
  onApply,
  onConfirm,
  onFollowUp,
  onPrep,
}) => {
  if (step.key === 'needs_apply') {
    return (
      <InteractiveStepPill
        onClick={onApply}
        title="Apply to this job"
        loading={applying}
        tone="action"
        icon={<Send className="w-3 h-3 shrink-0" />}
        label={applying ? 'Opening…' : 'Apply'}
      />
    );
  }
  if (step.key === 'confirm_apply') {
    return (
      <InteractiveStepPill
        onClick={() => onConfirm('applied')}
        title="Confirm you submitted this application"
        loading={confirming}
        tone="action"
        pulse={step.pulse}
        icon={<Hourglass className="w-3 h-3 shrink-0" />}
        label={confirming ? 'Saving…' : 'Confirm'}
      />
    );
  }
  if (step.key === 'followup_due') {
    return (
      <InteractiveStepPill
        onClick={onFollowUp}
        title="Send follow-up email"
        loading={sendingReminder}
        tone="action"
        icon={<Mail className="w-3 h-3 shrink-0" />}
        label="Follow up"
      />
    );
  }
  if (step.key === 'interview') {
    return (
      <InteractiveStepPill
        onClick={onPrep}
        title="Interview prep"
        tone="action"
        icon={<Zap className="w-3 h-3 shrink-0" />}
        label="Prep"
      />
    );
  }
  return <StatusPill step={step} />;
};

const RowActionsMenu: React.FC<{
  jobTitle: string;
  onPreview: () => void;
  onPortal: () => void;
  extraItems: HoverMenuItem[];
}> = ({ jobTitle, onPreview, onPortal, extraItems }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={`Actions for ${jobTitle}`}
        aria-expanded={open}
        className="ds-activity-row-actions inline-flex items-center justify-center w-8 h-8 rounded-lg border vscode-border vscode-bg-primary hover:vscode-bg-hover transition-all vscode-text-secondary"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-xl border vscode-border vscode-bg-secondary shadow-lg py-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); onPreview(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm vscode-text-primary hover:vscode-bg-hover transition-colors text-left"
          >
            <Eye className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
            Preview resume
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); onPortal(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm vscode-text-primary hover:vscode-bg-hover transition-colors text-left"
          >
            <ExternalLink className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" />
            Open job portal
          </button>
          {extraItems.length > 0 && <div className="my-1 border-t vscode-border" />}
          {extraItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); item.onClick(); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:vscode-bg-hover transition-colors text-left ${item.danger ? 'text-red-500 dark:text-red-400' : 'vscode-text-primary'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(OptimizationHistory);
